import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert,
  WifiOff,
  ArrowLeft,
  RefreshCw,
  LayoutDashboard,
} from "lucide-react";

type AdminAccessStateProps = {
  pageTitle: string;
  variant: "denied" | "unavailable";
  onRetry?: () => void;
  retrying?: boolean;
};

export const AdminAccessState: React.FC<AdminAccessStateProps> = ({
  pageTitle,
  variant,
  onRetry,
  retrying = false,
}) => {
  const navigate = useNavigate();
  const toggleMobileRef = useRef<(() => void) | null>(null);

  const isUnavailable = variant === "unavailable";
  const Icon = isUnavailable ? WifiOff : ShieldAlert;
  const iconWrapClass = isUnavailable
    ? "bg-amber-50 text-amber-600 ring-amber-100"
    : "bg-blue-50 text-blue-600 ring-blue-100";

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
      <Sidebar
        registerMobileToggle={(fn) => {
          toggleMobileRef.current = fn;
        }}
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <PageHeader
          pageTitle={pageTitle}
          onMenuClick={() => toggleMobileRef.current?.()}
        />

        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md text-center">
            <div
              className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ring-8 ${iconWrapClass}`}
            >
              <Icon className="h-8 w-8" />
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              {isUnavailable ? "Unable to connect" : "Access restricted"}
            </h1>

            <p className="mt-3 text-sm leading-relaxed text-gray-500">
              {isUnavailable ? (
                <>
                  We couldn&apos;t reach the server right now. Check that the app
                  is running and your internet connection is working, then try
                  again.
                </>
              ) : (
                <>
                  This page is available to administrators only. If you need
                  access, contact your account manager or return to your
                  dashboard.
                </>
              )}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              {isUnavailable && onRetry && (
                <Button
                  className="w-full sm:w-auto gap-2"
                  onClick={onRetry}
                  disabled={retrying}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${retrying ? "animate-spin" : ""}`}
                  />
                  {retrying ? "Retrying..." : "Try again"}
                </Button>
              )}

              <Button
                variant={isUnavailable ? "outline" : "default"}
                className="w-full sm:w-auto gap-2"
                onClick={() => navigate("/dashboard")}
              >
                <LayoutDashboard className="h-4 w-4" />
                Go to Dashboard
              </Button>

              {!isUnavailable && (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto gap-2"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Go back
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
